import { AnalyticsProps } from '../../InternalLibrary/analytics';
import {
  default as FieldGroup,
  FieldGroupProps,
} from 'components/atoms/FieldGroup/FieldGroup';
import { default as Input, InputProps } from 'components/atoms/Input/Input';
import { useCallback, useRef } from 'react';
import { useFormik } from 'formik';
import { useValidationText } from 'hooks/useValidationText';
import useEventCollector from '../../hooks/eventCollector/useEventCollector';

export interface InputActorProps extends AnalyticsProps {
  className?: string;
  formik: ReturnType<typeof useFormik<any>>;
  name: string;
  label: string;
  fieldGroupProps?: Omit<FieldGroupProps, 'children'>;
  inputProps?: Partial<InputProps>;
  sanitizer?: (value: string) => string;
  formatter?: (value: string) => string;
}

export const InputActor: React.FC<InputActorProps> = ({
  className,
  formik,
  name,
  label,
  fieldGroupProps = {},
  inputProps = {},
  sanitizer,
  formatter,
}) => {
  const valueRef = useRef(formik.values[name]);
  const { track } = useEventCollector();

  const onChange = useCallback(
    (e: any) => {
      formik.status?.errors?.[name] && delete formik.status.errors[name];
      formik.handleChange(e);

      if (sanitizer) {
        formik.setFieldValue(name, sanitizer(e.target.value));
      }

      valueRef.current = e.target.value;

      if (inputProps?.onChange) {
        inputProps?.onChange(e);
      }
    },
    [formik, name, sanitizer, track],
  );

  const validationErrorText = useValidationText(formik, name);

  return (
    <FieldGroup
      className={className}
      validationText={validationErrorText}
      {...fieldGroupProps}
    >
      <Input
        {...inputProps}
        dataTest={`input-${name}`}
        label={label}
        name={name}
        onChange={onChange}
        error={validationErrorText !== undefined}
        onBlur={formik.handleBlur}
        value={formik.values[name]}
        formatter={formatter}
      />
    </FieldGroup>
  );
};
